import { useEffect, useState } from 'react';
import type { QuoteView } from '@contracts';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import { Repeat, Bookmark, Check } from '@ui/shared/icons';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { QUOTES } from '@domain/content/quotes';
import { TodayCard } from './primitives';

/**
 * Row A — Sentence of the day (R8.7, design/screens/today-desktop-v2.md
 * SS6). "Shuffle" is PURE client-side selection over the curated `QUOTES`
 * set (no event, no `DomainApi` call — contract's own `SentenceOfDayState`
 * doc comment); "Save" calls `saveQuote`/reads `getSentenceOfDayState` for
 * the CURRENTLY-SHOWN quote only.
 */
export function SentenceOfDayCard() {
  const api = useDomainApi();
  const [quote, setQuote] = useState<QuoteView | null>(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setQuote(pickInitialQuote());
  }, []);

  useEffect(() => {
    if (!quote) return;
    let cancelled = false;
    setLoading(true);
    void api.getSentenceOfDayState(quote.quoteId).then((state) => {
      if (cancelled) return;
      setSaved(state.saved);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [api, quote]);

  function shuffle() {
    setQuote((current) => pickDifferentQuote(current));
  }

  function save() {
    if (!quote) return;
    void api.saveQuote(quote.quoteId).then(() => setSaved(true));
  }

  if (!quote || loading) {
    return (
      <TodayCard label="Sentence of the day">
        <SkeletonBlock height={72} />
      </TodayCard>
    );
  }

  return (
    <TodayCard label="Sentence of the day">
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-5)' }}>
        <span
          aria-hidden="true"
          style={{
            flex: 'none',
            fontSize: '40px',
            lineHeight: 1,
            fontWeight: 700,
            color: 'var(--border-strong)',
          }}
        >
          &ldquo;
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 'var(--type-body-size)', lineHeight: 1.5, color: 'var(--text-primary)' }}>
            {quote.text}
          </p>
          <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
            — {quote.author}
          </div>
        </div>
        <div style={{ display: 'flex', flex: 'none', gap: 'var(--space-3)' }}>
          <button
            type="button"
            onClick={shuffle}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-2)',
              color: 'var(--text-secondary)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 600,
            }}
          >
            <Repeat size={16} aria-hidden="true" />
            Shuffle
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saved}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-2)',
              color: saved ? 'var(--hue-primary)' : 'var(--text-secondary)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 600,
            }}
          >
            {saved ? <Check size={16} aria-hidden="true" /> : <Bookmark size={16} aria-hidden="true" />}
            {saved ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>
    </TodayCard>
  );
}

function pickInitialQuote(): QuoteView {
  const index = Math.floor(Math.random() * QUOTES.length);
  return QUOTES[index] ?? QUOTES[0]!;
}

/** Cycles to a different quote — never repeats the immediately-prior quote when the set has >1 entry (AC-T18). */
function pickDifferentQuote(current: QuoteView | null): QuoteView {
  if (QUOTES.length <= 1) return QUOTES[0]!;
  let next = current;
  while (!next || next.quoteId === current?.quoteId) {
    const index = Math.floor(Math.random() * QUOTES.length);
    next = QUOTES[index] ?? QUOTES[0]!;
  }
  return next;
}
